import {type FC, memo, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useLocation} from 'react-router';

import type {UserApiModel as User} from '@yourcoach/shared/api/user';

import {i18n} from '@yourcoach/i18n';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {alert} from '@yourcoach/shared/components/alert';
import {Text} from '@yourcoach/shared/uikit/Text';
import {View} from '@yourcoach/shared/uikit/View';
import {logger} from '@yourcoach/shared/utils/logger';
import {openUrlInANewTab} from '@yourcoach/shared/utils/openUrlInANewTab';
import {storage} from '@yourcoach/shared/utils/storage';

import Loader from '@src/components/Loader/Loader';
import LogoOnlyLayout from '@src/layouts/LogoOnly';
import {routerPath} from '@src/routes/path';

const CONNECT_STRIPE_STORAGE_KEY = 'connect_stripe';

const REDIRECT_URI_REGEXP = /redirect_uri=.*?&/g;

type Config = {
  fromScreen: string;
};

export const connectStripe = ({fromScreen}: Config) =>
  new Promise<boolean>(resolve => {
    const handleStripeConnect = async (e: StorageEvent) => {
      if (e.key === CONNECT_STRIPE_STORAGE_KEY) {
        const savedState = (await storage.getItem(
          CONNECT_STRIPE_STORAGE_KEY,
        )) as string;

        try {
          const {result: isStripeConnected} = JSON.parse(savedState) as {
            result: boolean;
          };

          if (isStripeConnected) {
            // the window can't be closed in Chrome anymore
            // stripeWindow?.close();

            await currentUserStore.localUpdate({
              is_provider: true,
            });

            await currentUserStore.fetch();

            window.removeEventListener('storage', handleStripeConnect);
          }

          resolve(isStripeConnected);
        } catch (error) {
          resolve(false);
        }
      }
    };

    window.addEventListener('storage', handleStripeConnect);

    const stripeWindow = openUrlInANewTab(
      routerPath.connectStripe.$buildPath({
        searchParams: {
          from: fromScreen,
        },
      }),
    );

    stripeWindow?.focus();

    const timerId = setInterval(() => {
      try {
        if (stripeWindow?.closed) {
          clearInterval(timerId);
          window.removeEventListener('storage', handleStripeConnect);
          resolve(false);
        }
      } catch (error) {
        // do nothing
      }
    }, 250);
  });

const askForStripeAccountType = () =>
  alert.show<User['provider_type']>({
    buttons: [
      {
        label: i18n.t('shared:no'),
        onAwaitableClick: resolve => resolve('express'),
      },
      {
        label: i18n.t('shared:yes'),
        onAwaitableClick: resolve => resolve('standard'),
      },
    ],
    title: i18n.t('web-app:ConnectStripe.existing_stripe_account_question'),
  });

interface Props {}

const ConnectStripePage: FC<Props> = () => {
  const query = new URLSearchParams(useLocation().search);

  const code = query.get('code');
  const state = query.get('state');
  const stripeError = query.get('error');
  const fromScreen = query.get('from') ?? '';

  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const mount = async () => {
      if (stripeError) {
        alert.show({
          title: i18n.t('shared:message.error'),
        });

        return;
      }

      if (code && state) {
        try {
          const savedState = (await storage.getItem(
            CONNECT_STRIPE_STORAGE_KEY,
          )) as string;

          const {redirectUri} = savedState.startsWith('{')
            ? JSON.parse(savedState)
            : ({} as any);

          if (redirectUri) {
            const urlSend = `${decodeURIComponent(redirectUri)}${
              window.location.search
            }`;

            // in dev env you need to disable CORS in your browser
            await fetch(urlSend);

            setIsSuccess(true);
          }

          storage.setItem(
            CONNECT_STRIPE_STORAGE_KEY,
            JSON.stringify({
              result: true,
            }),
          );
        } catch (error) {
          alert.error(error);

          logger.error(error);
        }
      } else {
        const providerType = await askForStripeAccountType();

        logger.event('stripe_account_type_tap', {
          result: providerType === 'express' ? 'no' : 'yes',
          screen: fromScreen,
        });

        const url = await currentUserStore.getProviderUrl({
          isExpress: providerType === 'express',
        });

        const match = url.match(REDIRECT_URI_REGEXP);

        let redirectUri = '';

        if (match) {
          redirectUri = match[0].replace('redirect_uri=', '').replace('&', '');
        }

        storage.setItem(
          CONNECT_STRIPE_STORAGE_KEY,
          JSON.stringify({
            providerType,
            redirectUri,
            result: false,
          }),
        );

        const newRedirectUri = `redirect_uri=${encodeURIComponent(
          `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
        )}&`;

        const newUrl = url.replace(REDIRECT_URI_REGEXP, newRedirectUri);

        window.location.replace(newUrl);
      }
    };

    mount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LogoOnlyLayout v2>
      <Helmet title={i18n.t('web-app:ConnectStripe.title')} />
      <div className="ConnectStripePage">
        {isSuccess ? (
          <View
            style={{
              alignItems: 'center',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Text variant="h1">
              {i18n.t('web-app:ConnectStripe.success_title')}
            </Text>
            <Text color="text3" variant="h2">
              {i18n.t('web-app:ConnectStripe.success_message')}
            </Text>
          </View>
        ) : (
          <Loader />
        )}
      </div>
    </LogoOnlyLayout>
  );
};

export default memo(ConnectStripePage);
